import { createElement, forwardRef } from 'react'
import type { ForwardRefExoticComponent, RefAttributes, SVGProps } from 'react'

/**
 * The icon runtime behind the vendored Lucide set (`packages/bloo-ui/icons/`).
 *
 * This replaces the `lucide-react` dependency with ~40 lines that do the same
 * job, so the icons live in this repository rather than in a package we pull
 * at install time. See `docs/icons.md` for why, and for the licence terms the
 * vendored art ships under (ISC, plus MIT for the Feather-derived subset).
 *
 * The render is a faithful port of lucide-react 1.33.0's `Icon` — same default
 * attributes, same `size`/`strokeWidth` handling, same `lucide lucide-<slug>`
 * class names, same accessibility default. Deliberately dropped: the
 * `LucideContext` provider and `DynamicIcon`, neither of which this console
 * has ever used.
 */

/** `[tag, attributes]` pairs, exactly as the SVG source declares them. */
export type IconNode = Array<[string, Record<string, string | number>]>

export interface IconComponentProps extends Omit<SVGProps<SVGSVGElement>, 'ref'> {
  size?: number | string
  /**
   * Keep the stroke visually constant as the icon scales, instead of scaling
   * with it. Lucide's own semantics, kept for parity.
   */
  absoluteStrokeWidth?: boolean
}

export type IconComponent = ForwardRefExoticComponent<
  IconComponentProps & RefAttributes<SVGSVGElement>
>

const DEFAULT_ATTRIBUTES = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const

/**
 * An icon carrying no label and no children is decoration, and must be hidden
 * from assistive tech — but only when the caller has not already said
 * otherwise. `Icon` (the design-system wrapper) sets `aria-label`/`role` when
 * a label is given, and this check is what stops that from being overridden.
 */
function hasA11yProp(props: Record<string, unknown>): boolean {
  for (const key in props) {
    if (key.startsWith('aria-') || key === 'role' || key === 'title') return true
  }
  return false
}

function cx(...parts: Array<string | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

export function createIcon(slug: string, iconNode: IconNode): IconComponent {
  const Component = forwardRef<SVGSVGElement, IconComponentProps>(
    ({ color, size, strokeWidth, absoluteStrokeWidth, className, children, ...rest }, ref) => {
      const stroke = absoluteStrokeWidth
        ? (Number(strokeWidth ?? DEFAULT_ATTRIBUTES.strokeWidth) * 24) /
          Number(size ?? DEFAULT_ATTRIBUTES.width)
        : (strokeWidth ?? DEFAULT_ATTRIBUTES.strokeWidth)

      return createElement(
        'svg',
        {
          ref,
          ...DEFAULT_ATTRIBUTES,
          width: size ?? DEFAULT_ATTRIBUTES.width,
          height: size ?? DEFAULT_ATTRIBUTES.height,
          stroke: color ?? DEFAULT_ATTRIBUTES.stroke,
          strokeWidth: stroke,
          className: cx('lucide', `lucide-${slug}`, className),
          ...(!children && !hasA11yProp(rest) ? { 'aria-hidden': 'true' } : {}),
          ...rest,
        },
        [
          ...iconNode.map(([tag, attrs], i) => createElement(tag, { key: `${tag}-${i}`, ...attrs })),
          ...(Array.isArray(children) ? children : [children]),
        ],
      )
    },
  )
  Component.displayName = slug
  return Component
}
